'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faClock, faDownload, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';

interface DownloadLimitReachedModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeUntilReset?: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  redirectToPlans?: boolean;
}

export default function DownloadLimitReachedModal({
  isOpen,
  onClose,
  timeUntilReset,
  redirectToPlans = true
}: DownloadLimitReachedModalProps) {
  const router = useRouter();
  const [countdown, setCountdown] = useState(timeUntilReset);

  useEffect(() => {
    if (!isOpen || !timeUntilReset) return;

    setCountdown(timeUntilReset);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (!prev) return prev;

        let { hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else {
          clearInterval(interval);
          return { hours: 0, minutes: 0, seconds: 0 };
        }

        return { hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, timeUntilReset]);

  const handleUpgrade = () => {
    onClose();
    if (redirectToPlans) {
      router.push('/planos');
    }
  };

  if (!isOpen) return null;

  const formatTime = (value: number) => String(value).padStart(2, '0');

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-[9998] animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4 text-gray-600" />
          </button>

          {/* Content */}
          <div className="p-8 text-center">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 mb-6">
              <FontAwesomeIcon icon={faDownload} className="w-8 h-8 text-orange-600" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Limite de Downloads Atingido
            </h2>

            {/* Message */}
            <p className="text-gray-600 mb-6">
              Você atingiu o limite do seu plano atual.
              {countdown && (
                <>
                  {' '}Seus limites serão resetados em:
                </>
              )}
            </p>

            {/* Countdown */}
            {countdown && (
              <div className="flex items-center justify-center gap-2 mb-6">
                <FontAwesomeIcon icon={faClock} className="w-5 h-5 text-orange-600" />
                <div className="flex items-center gap-1">
                  <div className="bg-orange-100 rounded-lg px-3 py-2">
                    <span className="text-2xl font-bold text-orange-600">
                      {formatTime(countdown.hours)}
                    </span>
                  </div>
                  <span className="text-xl font-bold text-gray-400">:</span>
                  <div className="bg-orange-100 rounded-lg px-3 py-2">
                    <span className="text-2xl font-bold text-orange-600">
                      {formatTime(countdown.minutes)}
                    </span>
                  </div>
                  <span className="text-xl font-bold text-gray-400">:</span>
                  <div className="bg-orange-100 rounded-lg px-3 py-2">
                    <span className="text-2xl font-bold text-orange-600">
                      {formatTime(countdown.seconds)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-800 font-medium">
                💡 Upgrade seu plano para ter acesso a mais downloads diários!
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={handleUpgrade}
                className={clsx(
                  "flex-1 px-6 py-3 rounded-xl font-semibold transition-all",
                  "bg-gradient-to-r from-orange-500 to-orange-600 text-white",
                  "hover:from-orange-600 hover:to-orange-700",
                  "shadow-lg hover:shadow-xl",
                  "flex items-center justify-center gap-2"
                )}
              >
                Ver Planos
                <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
